// Stale-chunk detection + one-time recovery reload (impure wrapper).
//
// After a deploy, the SPA's lazy route chunks get new content-hashed
// filenames and the previous ones are dropped from the server. A browser tab
// still running the OLD index.js requests an old chunk hash when the user
// navigates to a not-yet-loaded route; the functions/ asset-miss guard 404s
// the missing /assets/*.js (X-Asset-Miss, no-store), so the dynamic import
// rejects with "Failed to fetch dynamically imported module". index.html is
// served no-store, so a single full reload pulls the fresh shell + chunk graph
// and the navigation succeeds.
//
// The decide-reload-or-surface logic is a PURE helper (src/lib/boot/
// staleChunkReload.ts, unit-tested); this module owns the impure parts:
// reading the current bundle version + sessionStorage guard, recording
// reliability telemetry BEFORE the reload (so Error Center keeps visibility),
// and calling location.reload(). Shared by the global error/rejection handlers,
// the Vite `vite:preloadError` handler, and both React error boundaries (in
// main.tsx and App.tsx) so every path recovers the same way behind one guard.

import {
  decideStaleChunkReload,
  isStaleChunkError,
  staleChunkReloadKey,
} from '../lib/boot/staleChunkReload'
import { reportClientEvent } from '../reliability'

export { isStaleChunkError } from '../lib/boot/staleChunkReload'

// How long after a recovery reload we wait before clearing the one-shot guard
// on a successful boot. If the previously-failing route re-imports its (now
// fresh) chunk without another failure inside this window, the recovery worked
// and we restore the single-reload budget for FUTURE stale chunks. If it
// re-fails inside the window, the surface path cancels this clear so the guard
// persists and a broken deploy can never loop.
const SETTLE_MS = 12_000

let pendingClearTimer: ReturnType<typeof setTimeout> | null = null

// Current deployed bundle "version" = the entry bundle's content hash, read
// from the module script the browser is actually running. In dev there is no
// hash, so we fall back to 'dev'. Never throws.
function currentBundleVersion(): string {
  try {
    const scripts = document.querySelectorAll('script[type="module"][src]')
    for (const s of Array.from(scripts)) {
      const src = (s as HTMLScriptElement).src || ''
      const m = src.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/)
      if (m) return m[1]
    }
  } catch {
    /* ignore */
  }
  return 'dev'
}

// Core: given a (possibly forced-stale) failure, run the pure decision against
// the live version + URL + guard flag, and either reload once (recording
// telemetry first) or return false so the caller lets the error surface.
function runStaleChunkRecovery(
  message: unknown,
  assumeStale: boolean,
  telemetry: Record<string, unknown>,
): boolean {
  if (typeof window === 'undefined') return false

  const version = currentBundleVersion()
  const href = window.location.href
  const key = staleChunkReloadKey(version, href)

  let hasReloadedFlag = false
  try {
    hasReloadedFlag = !!sessionStorage.getItem(key)
  } catch {
    // No usable guard store => never reload; without a guard a genuinely
    // broken deploy would reload-loop. Let the error surface instead.
    return false
  }

  const decision = decideStaleChunkReload({
    message,
    assumeStale,
    bundleVersion: version,
    href,
    hasReloadedFlag,
  })

  if (!decision.isStaleChunk) return false

  if (!decision.shouldReload) {
    // Already reloaded once for this exact bundle+URL and it STILL failed =>
    // a genuinely-broken deploy, not a stale tab. Keep the guard (cancel any
    // pending clear) and let the error surface so the user / Error Center see
    // the real failure instead of a reload loop.
    if (pendingClearTimer != null) {
      clearTimeout(pendingClearTimer)
      pendingClearTimer = null
    }
    return false
  }

  try {
    sessionStorage.setItem(decision.key, '1')
  } catch {
    return false
  }

  // Telemetry FIRST — beacon so it survives the imminent reload, keeping the
  // Error Center's visibility into stale-chunk auto-recoveries.
  try {
    reportClientEvent(
      'stale_chunk_reload',
      { ...telemetry, bundleVersion: version, route: window.location.hash || '#/' },
      'warn',
      true,
    )
  } catch {
    /* never block recovery on telemetry */
  }

  window.location.reload()
  return true
}

// Triggered from the global `error` / `unhandledrejection` handlers and both
// React error boundaries. Returns true when a recovery reload was kicked off so
// callers can stop further handling. Only acts on stale-chunk messages.
export function reloadOnceForStaleChunk(msg: unknown): boolean {
  if (typeof window === 'undefined' || !isStaleChunkError(msg)) return false
  return runStaleChunkRecovery(msg, false, { source: 'error', message: String(msg ?? '') })
}

// Triggered from Vite's `vite:preloadError` window event, which fires on ANY
// dynamic-import / module-preload failure. That event is by definition a chunk
// load failure, so we force-treat it as stale (its payload message is not
// reliably shaped like the strings isStaleChunkError matches).
export function reloadOnceForPreloadError(payload: unknown): boolean {
  if (typeof window === 'undefined') return false
  const message = (payload as { message?: unknown } | null | undefined)?.message
  return runStaleChunkRecovery(String(message ?? 'vite:preloadError'), true, {
    source: 'vite:preloadError',
    message: String(message ?? 'vite:preloadError'),
  })
}

// Called once at boot (from main.tsx). If the one-shot guard is set for the
// CURRENT version+URL, we just reloaded here — give the previously-failing
// route a settle window to re-import its now-fresh chunk, then clear the guard
// so a FUTURE stale chunk gets its own budget. A re-failure inside the window
// cancels this clear (see runStaleChunkRecovery), so recovery is loop-safe.
export function noteSuccessfulBoot(): void {
  if (typeof window === 'undefined') return
  let key = ''
  try {
    key = staleChunkReloadKey(currentBundleVersion(), window.location.href)
    if (!sessionStorage.getItem(key)) return
  } catch {
    return
  }
  if (pendingClearTimer != null) clearTimeout(pendingClearTimer)
  pendingClearTimer = setTimeout(() => {
    pendingClearTimer = null
    try {
      sessionStorage.removeItem(key)
    } catch {
      /* ignore */
    }
  }, SETTLE_MS)
}
